import debug from 'debug';
import router from '@/router';
import loginService from './login.service';
import eventService from '../../services/event';
import cookieService from '../../services/cookie';
import logger from '../../lib/logger';
import globals from '../../globals';

const log = debug('store:login');

function getBestPricing({ result, preview }) {
  if (['PREAPPROVED', 'APPROVED'].includes(result)
    && Array.isArray(preview.downPaymentOptions)
    && preview.downPaymentOptions.length > 0) {
    const lastDpOpt = preview.downPaymentOptions[preview.downPaymentOptions.length - 1];
    if (Array.isArray(lastDpOpt.installments) && lastDpOpt.installments.length > 0) {
      const lastInstallment = lastDpOpt.installments[lastDpOpt.installments.length - 1];
      const { downPaymentPercentage } = lastDpOpt;
      const { interest } = lastInstallment;
      return {
        pmt: lastInstallment.value,
        installments: lastInstallment.installments,
        interest: parseFloat((interest / 100).toFixed(2)),
        downPaymentPercentage,
        downPayment: downPaymentPercentage > 0,
      };
    }
  }
  return null;
}

function filterValue({ field, value }) {
  const filters = {
    phone: value && value.replace(/[() -]/g, ''),
    cpf: value && value.replace(/[.-]/g, ''),
    zipCode: value && value.replace('-', ''),
  };
  return filters[field] || value;
}

function sanitizeUser({ user }) {
  return {
    cpf: filterValue({ field: 'cpf', value: user.cpf }),
    phone: filterValue({ field: 'phone', value: user.phone }),
    email: user.email,
    zipCode: filterValue({ field: 'zipCode', value: user.zipCode }),
  };
}

function hideEmail(email) {
  if (!email) return email;
  const split = email.split('@');
  const first = split[0];
  if (!first) return email;
  const asteriskNumber = 6;
  const asterisk = Array(asteriskNumber).join('*');
  const username = `${first.substring(0, 2)}${asterisk}${first.substring(first.length - 2)}`;
  split[0] = username;
  return split.join('@');
}

function hiddenPhone(phone) {
  if (!phone) return phone;

  const cleanPhoneString = phone.replace(/[^\d]/g, '');
  const formatedPhone = cleanPhoneString.replace(/(\d{2})(\d{8})/, '($1) $2');

  return `${formatedPhone.substring(0, 9)} *****`;
}

export default {
  namespaced: true,
  state: {
    user: {
      cpf: null,
      email: null,
      phone: null,
      zipCode: null,
    },
    hiddenEmail: null,
    endPhone: null,
    preApproved: null,
    maxCreditValue: 5000,
    result: null,
    name: null,
    bestPricing: {
      installments: globals().maxInstallments,
      interest: 0.052,
      downPaymentPercentage: 30,
    },
    source: 'login',
    apiKey: globals().apiKey,
    trackId: null,
    loading: false,
    loadingMessage: '',
  },
  actions: {
    setLoading({ commit }, { loading, message }) {
      commit('setLoading', loading);
      if (message) {
        commit('setLoadingMessage', message);
      }
    },
    async getLogin({ commit, state }) {
      log('setLogin', state.user.cpf);

      try {
        const { data } = await loginService
          .checkUserCpf({
            cpf: filterValue({ field: 'cpf', value: state.user.cpf }),
          });
        commit('setHiddenEmail', data.hiddenEmail);
        commit('setEndPhone', data.endPhone);
        return { success: true };
      } catch (error) {
        if (error && error.response && error.response.data === 'user not found') {
          router.push('/login/verificacao?origin=signIn');
        } else {
          logger.error(error);
        }
        return { success: false };
      }
    },
    async getPreApprove({ state, commit }) {
      try {
        const campaign = cookieService.readCookie({ name: 'prclx_campaign' });

        const { data } = await loginService.preApprove({
          apiKey: state.apiKey,
          campaign,
          ...sanitizeUser({ user: state.user }),
        });

        const { result, preApprovedCredit, preview, trackId } = data;

        const bestPricing = getBestPricing({ result, preview });

        commit('setPreApprove', {
          preApproved: preview && preview.preApproved,
          maxCreditValue: (preApprovedCredit && preApprovedCredit.maxValue) || 5000,
          result,
          bestPricing,
          name: preApprovedCredit
            && preApprovedCredit.checkoutData
            && preApprovedCredit.checkoutData.name,
        });

        commit('setTrackId', trackId);

        if (preApprovedCredit && preApprovedCredit.email && preApprovedCredit.phone) {
          commit('setHiddenEmail', hideEmail(preApprovedCredit.email));
          commit('setEndPhone', hiddenPhone(preApprovedCredit.phone));
        } else {
          commit('setHiddenEmail', hideEmail(state.user.email));
          commit('setEndPhone', hiddenPhone(state.user.phone));
        }
      } catch (error) {
        if (error && error.response && error.response.status === 400) {
          if ([
            'ValidationError: child "email" fails because ["email" must be a valid email]',
            'invalid email',
          ].includes(error.response.data)) {
            throw new Error('invalid email');
          }
        }
        logger.error(error);
        router.push('/login/error');
      }
    },
    async sendLink({ state }, { method }) {
      try {
        await loginService.sendAccessLinkByMethod({
          method,
          cpf: filterValue({ field: 'cpf', value: state.user.cpf }),
        });
      } catch (error) {
        logger.error(error);
      }
    },
    async sendEvent({ state }, { type }) {
      try {
        if (state.source === 'site') {
          eventService.logSiteEvents({
            type,
            data: {
              ...state,
              user: sanitizeUser({ user: state.user }),
            },
          });
        } else {
          eventService.logLoginEvents({
            type,
            data: {
              ...state,
              user: sanitizeUser({ user: state.user }),
            },
          });
        }
      } catch (error) {
        logger.error(error);
      }
    },
    async sendEventLog({ state }, { action, origin }) {
      try {
        eventService.logDeniedEvents({
          action,
          origin,
          data: {
            ...state,
            user: sanitizeUser({ user: state.user }),
          },
        });
      } catch (error) {
        logger.error(error);
      }
    },
    setHiddenEmail({ commit }, hiddenEmail) {
      commit('setHiddenEmail', hiddenEmail);
    },
    setEndPhone({ commit }, endPhone) {
      commit('setEndPhone', endPhone);
    },
    setUserData({ commit }, { field, value }) {
      commit('setUserData', { field, value });
    },
    setSource({ commit }, source) {
      commit('setSource', source);
    },
    clearUserData({ commit }) {
      commit('clearUserData');
    },
  },
  mutations: {
    setHiddenEmail(state, hiddenEmail) {
      state.hiddenEmail = hiddenEmail;
    },
    setUserData(state, { field, value }) {
      state.user[field] = value;
    },
    setEndPhone(state, endPhone) {
      state.endPhone = endPhone;
    },
    setPreApprove(state, { preApproved, maxCreditValue, result, bestPricing, name }) {
      state.preApproved = preApproved;
      state.maxCreditValue = maxCreditValue;
      state.result = result;
      state.bestPricing = bestPricing;
      state.name = name;
    },
    setSource(state, source) {
      state.source = source;
    },
    setTrackId(state, trackId) {
      state.trackId = trackId;
    },
    setLoading(state, loading) {
      if (loading === false);
      // setTimeout corrige bug que animação é interrompida subitamente
      setTimeout(() => { state.loading = loading; }, 100);
    },
    setLoadingMessage(state, message) {
      state.loadingMessage = message || '';
    },
    clearUserData(state) {
      state.user.cpf = null;
      state.user.email = null;
      state.user.phone = null;
      state.user.zipCode = null;
    },
  },
  getters: {
    getHiddenEmail: (state) => state.hiddenEmail,
    getEndPhone: (state) => state.endPhone,
  },
};
